import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import axios from '../../../services/axios';
import Loading from '../../../components/Loading';
import SectionMeta, { SectionHeaderRow } from '../../../components/SectionMeta';
import SalaryAdvanceModal from '../SalaryAdvanceModal/';
import PurchaseDeductionModal from '../PurchaseDeductionModal/';
import BenefitVoucherModal from '../BenefitVoucherModal/';
import DebtGroupDetailModal from '../DebtGroupDetailModal/';
import BenefitVoucherDetailModal from '../BenefitVoucherDetailModal/';
import MonthlyDeductionsModal from '../MonthlyDeductionsModal/';

import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionHeader,
   SectionBody,
   SectionTitle,
   SectionActions,
} from '../../../styles/layout/Section';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';
import { ActionBar, ActionBarRight } from '../../../styles/layout/ActionBar';
import { TableWrapper } from '../../../styles/table/TableWrapper';
import { Table } from '../../../styles/table/Table';
import { EmptyState } from '../../../styles/feedback/EmptyState';

// ─── Estilos locais ───────────────────────────────────────────────────────────

const EmployeeCard = styled.div`
   border: 1px solid rgba(0, 0, 0, 0.08);
   border-radius: 10px;
   margin-bottom: 10px;
   overflow: hidden;
`;

const EmployeeCardHeader = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 12px 16px;
   background: ${({ $isExpanded }) =>
      $isExpanded ? 'rgba(0,0,0,0.03)' : '#fff'};
   cursor: pointer;
   user-select: none;
   flex-wrap: wrap;

   &:hover {
      background: rgba(0, 0, 0, 0.03);
   }
`;

const EmployeeCardBody = styled.div`
   border-top: 1px solid rgba(0, 0, 0, 0.06);
`;

const TotalBadge = styled.span`
   display: inline-flex;
   align-items: center;
   padding: 2px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   background: rgba(2, 136, 209, 0.1);
   border: 1px solid rgba(2, 136, 209, 0.3);
   color: #01579b;
`;

const TypePill = styled.span`
   display: inline-flex;
   padding: 2px 8px;
   border-radius: 999px;
   font-size: 10px;
   font-weight: 500;
   background: ${({ $type }) =>
      $type === 'ADIANTAMENTO'
         ? '#96e3ac'
         : $type === 'COMPRA_EM_FOLHA'
           ? '#e0acf5'
           : '#d5bb83'};
   color: #373737;
`;

const TogglePillsGroup = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 6px;
   align-items: center;
   padding-top: 2px;
`;

const TogglePill = styled.button`
   display: inline-flex;
   align-items: center;
   padding: 4px 12px;
   border-radius: 999px;
   font-size: 0.72rem;
   font-weight: 600;
   cursor: pointer;
   border: 1px solid
      ${({ $active }) => ($active ? 'rgba(2,136,209,0.5)' : 'rgba(0,0,0,0.15)')};
   background: ${({ $active }) =>
      $active ? 'rgba(2,136,209,0.12)' : 'transparent'};
   color: ${({ $active }) => ($active ? '#01579b' : '#6b7280')};
   transition: all 120ms ease;

   &:hover {
      border-color: rgba(2, 136, 209, 0.4);
      color: #01579b;
   }
`;

const ActiveFilterChip = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 3px 10px;
   border-radius: 999px;
   font-size: 0.7rem;
   font-weight: 500;
   background: rgba(2, 136, 209, 0.1);
   border: 1px solid rgba(2, 136, 209, 0.25);
   color: #01579b;
`;

// ─── Dropdown de adição de débito ─────────────────────────────────────────────

const AddDebtButtonWrap = styled.div`
   position: relative;
   display: inline-block;
`;

const AddDebtDropdownMenu = styled.div`
   position: absolute;
   top: calc(100% + 6px);
   right: 0;
   min-width: 220px;
   background: #fff;
   border: 1px solid rgba(0, 0, 0, 0.08);
   border-radius: 10px;
   box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
   overflow: hidden;
   z-index: 50;
`;

const AddDebtDropdownItem = styled.button`
   width: 100%;
   text-align: left;
   padding: 10px 14px;
   font-size: 0.8rem;
   background: transparent;
   border: 0;
   cursor: pointer;
   color: #374151;

   &:hover {
      background: rgba(0, 0, 0, 0.04);
   }
`;

// ─── Constantes ───────────────────────────────────────────────────────────────

const EMPLOYMENT_STATUS_OPTIONS = [
   { value: 'ATIVO', label: 'Ativo' },
   { value: 'EXP 1', label: 'EXP 1' },
   { value: 'EXP 2', label: 'EXP 2' },
   { value: 'AFASTADO', label: 'Afastado' },
   { value: 'FÉRIAS', label: 'Férias' },
   { value: 'DESLIGADO', label: 'Desligado' },
];

const EMPLOYMENT_STATUS_LABEL = {
   ATIVO: 'Ativo',
   'EXP 1': 'EXP 1',
   'EXP 2': 'EXP 2',
   AFASTADO: 'Afastado',
   FÉRIAS: 'Férias',
   DESLIGADO: 'Desligado',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrencyBRL(numericValue) {
   if (numericValue === null || numericValue === undefined) return '—';
   return Number(numericValue).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

function formatMonthBR(isoDateString) {
   if (!isoDateString) return '—';
   const [year, month] = String(isoDateString).substring(0, 10).split('-');
   return `${month}/${year}`;
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function EmployeeDebtsReport() {
   const navigate = useNavigate();

   // ─── Filtros ─────────────────────────────────────────────────────────────
   const [filterByReferenceMonth, setFilterByReferenceMonth] = useState('');
   const [filterByEmploymentStatusList, setFilterByEmploymentStatusList] =
      useState([]);
   const [filterByDepartmentId, setFilterByDepartmentId] = useState('');
   const [filterByCityLocation, setFilterByCityLocation] = useState('');
   const [filterByEmployeeName, setFilterByEmployeeName] = useState('');
   const [filterByAdvanceStatus, setFilterByAdvanceStatus] = useState('ABERTO');
   const [filterByDebtDescription, setFilterByDebtDescription] = useState('');
   const [filterByOrderNumber, setFilterByOrderNumber] = useState('');
   const [filterByMinTotalAmount, setFilterByMinTotalAmount] = useState('');
   const [filterByMaxTotalAmount, setFilterByMaxTotalAmount] = useState('');
   const [appliedFilters, setAppliedFilters] = useState(null); // para comparar e mostrar chips de filtros ativos

   // ─── Dropdown "+ Adicionar débito" ────────────────────────────
   const [isAddDebtDropdownOpen, setIsAddDebtDropdownOpen] = useState(false);
   const addDebtDropdownRef = useRef(null);

   // ─── Modais de criação ────────────────────────────────────────
   const [isAdvanceModalOpen, setIsAdvanceModalOpen] = useState(false);
   const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
   const [isBenefitVoucherModalOpen, setIsBenefitVoucherModalOpen] =
      useState(false);

   // ─── Modal de detalhe do grupo pai ───────────────────────────
   const [debtGroupBeingViewed, setDebtGroupBeingViewed] = useState(null);
   // { debtType, employeeId, employeeName, groupId }

   // ─── Modal de detalhe do benefício ───────────────────────────
   const [benefitVoucherBeingViewed, setBenefitVoucherBeingViewed] =
      useState(null);
   // { voucher, employeeId, employeeName }

   // ─── Modal de PDF de descontos mensais ────────────────────────
   const [isMonthlyDeductionsModalOpen, setIsMonthlyDeductionsModalOpen] =
      useState(false);

   // ─── Ordenação ────────────────────────────────────────────────────────────
   const [sortConfig, setSortConfig] = useState({
      key: 'full_name',
      direction: 'ASC',
   });

   // ─── Dados ───────────────────────────────────────────────────────────────
   const [reportData, setReportData] = useState(null);
   const [isLoading, setIsLoading] = useState(false);

   // ─── Acordeons expandidos ─────────────────────────────────────────────────
   const [expandedEmployeeIds, setExpandedEmployeeIds] = useState(new Set());

   // ─── Dados auxiliares para selects ───────────────────────────────────────
   const [availableDepartments, setAvailableDepartments] = useState([]);

   useEffect(() => {
      async function loadDepartments() {
         try {
            const response = await axios.get('/hr/departments');
            setAvailableDepartments(response.data?.data?.departments ?? []);
         } catch {
            // silencioso
         }
      }
      loadDepartments();
   }, []);

   // Fechar dropdown ao clicar fora
   useEffect(() => {
      function handleClickOutside(event) {
         if (
            addDebtDropdownRef.current &&
            !addDebtDropdownRef.current.contains(event.target)
         ) {
            setIsAddDebtDropdownOpen(false);
         }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
         document.removeEventListener('mousedown', handleClickOutside);
   }, []);

   // ─── Multiselect de status do funcionário ─────────────────────────────────

   function handleEmploymentStatusToggle(statusValue) {
      setFilterByEmploymentStatusList((previousList) =>
         previousList.includes(statusValue)
            ? previousList.filter((item) => item !== statusValue)
            : [...previousList, statusValue]
      );
   }

   // ─── Chips de filtros ativos ───────────────────────────────────────────────
   function buildActiveFilterChips() {
      if (!appliedFilters) return [];
      const chips = [];
      if (appliedFilters.employeeName)
         chips.push(`Nome: ${appliedFilters.employeeName}`);
      if (appliedFilters.advanceStatus)
         chips.push(
            `Status débito: ${
               appliedFilters.advanceStatus === 'ABERTO'
                  ? 'Em aberto'
                  : appliedFilters.advanceStatus === 'QUITADO'
                    ? 'Quitado'
                    : 'Cancelado'
            }`
         );
      if (appliedFilters.referenceMonth)
         chips.push(
            `Mês: ${formatMonthBR(`${appliedFilters.referenceMonth}-01`)}`
         );
      if (appliedFilters.debtDescription)
         chips.push(`Título: ${appliedFilters.debtDescription}`);
      if (appliedFilters.orderNumber)
         chips.push(`Pedido: ${appliedFilters.orderNumber}`);
      if (appliedFilters.departmentId) {
         const department = availableDepartments.find(
            (d) => String(d.id) === String(appliedFilters.departmentId)
         );
         if (department) chips.push(`Depto: ${department.department_name}`);
      }
      if (appliedFilters.cityLocation)
         chips.push(`Praça: ${appliedFilters.cityLocation}`);
      if (appliedFilters.employmentStatusList?.length > 0) {
         chips.push(
            `Status func.: ${appliedFilters.employmentStatusList.map((s) => EMPLOYMENT_STATUS_LABEL[s] ?? s).join(', ')}`
         );
      }
      if (appliedFilters.minTotalAmount)
         chips.push(`Valor mín.: R$ ${appliedFilters.minTotalAmount}`);
      if (appliedFilters.maxTotalAmount)
         chips.push(`Valor máx.: R$ ${appliedFilters.maxTotalAmount}`);
      return chips;
   }

   // ─── Ordenação ────────────────────────────────────────────────────────────

   function getSortIcon(columnKey) {
      if (sortConfig.key !== columnKey) return ' ⇅';
      return sortConfig.direction === 'ASC' ? ' ▲' : ' ▼';
   }

   // ─── Busca ────────────────────────────────────────────────────────────────

   async function fetchReport(overrideSortConfig) {
      setIsLoading(true);

      setAppliedFilters({
         employeeName: filterByEmployeeName,
         advanceStatus: filterByAdvanceStatus,
         referenceMonth: filterByReferenceMonth,
         debtDescription: filterByDebtDescription,
         orderNumber: filterByOrderNumber,
         departmentId: filterByDepartmentId,
         cityLocation: filterByCityLocation,
         employmentStatusList: [...filterByEmploymentStatusList],
         minTotalAmount: filterByMinTotalAmount,
         maxTotalAmount: filterByMaxTotalAmount,
      });
      const activeSortConfig = overrideSortConfig ?? sortConfig;
      try {
         const queryParams = {};
         if (filterByReferenceMonth)
            queryParams.reference_month = filterByReferenceMonth;
         if (filterByEmploymentStatusList.length > 0)
            queryParams.employment_status = filterByEmploymentStatusList;
         if (filterByDepartmentId)
            queryParams.department_id = filterByDepartmentId;
         if (filterByCityLocation.trim())
            queryParams.city_location = filterByCityLocation.trim();
         if (filterByEmployeeName.trim())
            queryParams.employee_name = filterByEmployeeName.trim();
         if (filterByAdvanceStatus)
            queryParams.advance_status = filterByAdvanceStatus;
         if (filterByDebtDescription.trim())
            queryParams.debt_description = filterByDebtDescription.trim();
         if (filterByOrderNumber.trim())
            queryParams.order_number = filterByOrderNumber.trim();
         if (filterByMinTotalAmount)
            queryParams.min_total_amount = filterByMinTotalAmount;
         if (filterByMaxTotalAmount)
            queryParams.max_total_amount = filterByMaxTotalAmount;
         queryParams.sort_by = activeSortConfig.key;
         queryParams.sort_direction = activeSortConfig.direction;

         const response = await axios.get('/hr/employees/debts-report', {
            params: queryParams,
         });
         setReportData(response.data?.data ?? null);
         setExpandedEmployeeIds(new Set());
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao gerar relatório.';
         toast.error(errorMessage);
      } finally {
         setIsLoading(false);
      }
   }

   function handleSearchFormSubmit(event) {
      event.preventDefault();
      fetchReport();
   }

   function handleSortAndFetch(columnKey) {
      const newDirection =
         sortConfig.key === columnKey && sortConfig.direction === 'ASC'
            ? 'DESC'
            : 'ASC';
      const newSortConfig = { key: columnKey, direction: newDirection };
      setSortConfig(newSortConfig);
      if (reportData) fetchReport(newSortConfig);
   }

   function handleClearFilters() {
      setFilterByReferenceMonth('');
      setFilterByEmploymentStatusList([]);
      setFilterByDepartmentId('');
      setFilterByCityLocation('');
      setFilterByEmployeeName('');
      setFilterByAdvanceStatus('');
      setFilterByDebtDescription('');
      setFilterByOrderNumber('');
      setFilterByMinTotalAmount('');
      setFilterByMaxTotalAmount('');
      setSortConfig({ key: 'full_name', direction: 'ASC' });
      setReportData(null);
      setExpandedEmployeeIds(new Set());
      setAppliedFilters(null);
   }

   function handleToggleEmployeeExpansion(employeeId) {
      setExpandedEmployeeIds((previousExpandedIds) => {
         const updatedExpandedIds = new Set(previousExpandedIds);
         if (updatedExpandedIds.has(employeeId)) {
            updatedExpandedIds.delete(employeeId);
         } else {
            updatedExpandedIds.add(employeeId);
         }
         return updatedExpandedIds;
      });
   }

   function handleExpandAll() {
      if (!reportData?.employees) return;
      setExpandedEmployeeIds(
         new Set(reportData.employees.map((e) => e.employee_id))
      );
   }

   function handleCollapseAll() {
      setExpandedEmployeeIds(new Set());
   }

   // ─── Exportação ────────────────────────────────────────────────────────────
   function handleExportXlsx() {
      if (!reportData?.employees?.length) return;

      // Monta as linhas — uma por mês de referência por funcionário
      const rows = reportData.employees.flatMap((employee) => {
         // Agrupa parcelas por mês de referência
         const totalByMonth = {};
         employee.installments.forEach((installment) => {
            const monthKey = installment.reference_month;
            if (!totalByMonth[monthKey]) totalByMonth[monthKey] = 0;
            totalByMonth[monthKey] += installment.installment_amount;
         });

         return Object.entries(totalByMonth)
            .sort(([monthA], [monthB]) => monthA.localeCompare(monthB))
            .map(([referenceMonth, totalAmount]) => ({
               'Nome do funcionário': employee.full_name,
               'Mês de referência': formatMonthBR(referenceMonth),
               'Status do débito':
                  filterByAdvanceStatus === 'ABERTO'
                     ? 'Em aberto'
                     : filterByAdvanceStatus === 'QUITADO'
                       ? 'Quitado'
                       : 'Cancelado',
               'Valor total (R$)': parseFloat(totalAmount.toFixed(2)),
            }));
      });

      // Gera CSV como fallback caso SheetJS não esteja disponível
      // Aqui usamos a abordagem nativa sem dependência externa
      const headers = Object.keys(rows[0]);
      const csvLines = [
         headers.join(';'),
         ...rows.map((row) =>
            headers
               .map((header) => String(row[header]).replace(/;/g, ','))
               .join(';')
         ),
      ];
      const csvContent = '\uFEFF' + csvLines.join('\n'); // BOM para Excel abrir em UTF-8
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = `debitos_funcionarios_${filterByAdvanceStatus.toLowerCase()}_${new Date().toISOString().substring(0, 10)}.csv`;
      downloadLink.click();
      URL.revokeObjectURL(url);
   }
   // ─── Render ───────────────────────────────────────────────────────────────

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Débitos de funcionários</PageTitle>
               <PageSubtitle>
                  Visão geral de adiantamentos e compras em folha.
               </PageSubtitle>
            </PageHeaderText>

            {/* ─── Botão descontos mensais ─────────────────────── */}
            <button
               type="button"
               onClick={() => setIsMonthlyDeductionsModalOpen(true)}
            >
               🖨️ Descontos mensais
            </button>

            {/* ─── Dropdown "+ Adicionar débito" ──────────────── */}
            <AddDebtButtonWrap ref={addDebtDropdownRef}>
               <button
                  type="button"
                  onClick={() => setIsAddDebtDropdownOpen((prev) => !prev)}
               >
                  + Adicionar débito ▾
               </button>
               {isAddDebtDropdownOpen && (
                  <AddDebtDropdownMenu>
                     <AddDebtDropdownItem
                        onClick={() => {
                           setIsAddDebtDropdownOpen(false);
                           setIsAdvanceModalOpen(true);
                        }}
                     >
                        Novo adiantamento salarial
                     </AddDebtDropdownItem>
                     <AddDebtDropdownItem
                        onClick={() => {
                           setIsAddDebtDropdownOpen(false);
                           setIsPurchaseModalOpen(true);
                        }}
                     >
                        Nova compra com desconto em folha
                     </AddDebtDropdownItem>
                     <AddDebtDropdownItem
                        onClick={() => {
                           setIsAddDebtDropdownOpen(false);
                           setIsBenefitVoucherModalOpen(true);
                        }}
                     >
                        Novo benefício VA / VT
                     </AddDebtDropdownItem>
                  </AddDebtDropdownMenu>
               )}
            </AddDebtButtonWrap>
         </PageHeader>

         {/* ─── Filtros ─────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Pesquisa</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <form onSubmit={handleSearchFormSubmit}>
                  <FormGrid $cols={12} $colsMobile={12}>
                     {/* ── Linha 1 ── */}
                     <Field $col={3} $colMobile={6}>
                        <label>Nome do funcionário</label>
                        <input
                           type="text"
                           value={filterByEmployeeName}
                           onChange={(e) =>
                              setFilterByEmployeeName(e.target.value)
                           }
                           placeholder="Buscar por nome..."
                        />
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label>Título do débito</label>
                        <input
                           type="text"
                           value={filterByDebtDescription}
                           onChange={(e) =>
                              setFilterByDebtDescription(e.target.value)
                           }
                           placeholder="Ex: Compra moto, adiantamento..."
                        />
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label>Nº do pedido</label>
                        <input
                           type="text"
                           value={filterByOrderNumber}
                           onChange={(e) =>
                              setFilterByOrderNumber(e.target.value)
                           }
                           placeholder="Ex: OS 3240443"
                        />
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label>Mês de referência</label>
                        <input
                           type="month"
                           value={filterByReferenceMonth}
                           onChange={(e) =>
                              setFilterByReferenceMonth(e.target.value)
                           }
                        />
                     </Field>

                     {/* ── Linha 2 ── */}
                     <Field $col={3} $colMobile={6}>
                        <label>Status do débito</label>
                        <select
                           value={filterByAdvanceStatus}
                           onChange={(e) =>
                              setFilterByAdvanceStatus(e.target.value)
                           }
                        >
                           <option value="ABERTO">Em aberto</option>
                           <option value="QUITADO">Quitado</option>
                           <option value="CANCELADO">Cancelado</option>
                        </select>
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label>Departamento</label>
                        <select
                           value={filterByDepartmentId}
                           onChange={(e) =>
                              setFilterByDepartmentId(e.target.value)
                           }
                        >
                           <option value="">Todos</option>
                           {availableDepartments.map((department) => (
                              <option key={department.id} value={department.id}>
                                 {department.department_name}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label>Praça / Cidade</label>
                        <input
                           type="text"
                           value={filterByCityLocation}
                           onChange={(e) =>
                              setFilterByCityLocation(e.target.value)
                           }
                           placeholder="Ex: Goiânia"
                        />
                     </Field>

                     <Field $col={12} $colMobile={12}>
                        <label>Status do funcionário</label>
                        <TogglePillsGroup>
                           {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
                              <TogglePill
                                 key={option.value}
                                 type="button"
                                 $active={filterByEmploymentStatusList.includes(
                                    option.value
                                 )}
                                 onClick={() =>
                                    handleEmploymentStatusToggle(option.value)
                                 }
                              >
                                 {option.label}
                              </TogglePill>
                           ))}
                        </TogglePillsGroup>
                     </Field>

                     {/* ── Linha 3 — valor ── */}
                     <Field $col={3} $colMobile={6}>
                        <label>Valor total mínimo (R$)</label>
                        <input
                           type="number"
                           value={filterByMinTotalAmount}
                           onChange={(e) =>
                              setFilterByMinTotalAmount(e.target.value)
                           }
                           placeholder="0,00"
                           step="0.01"
                           min="0"
                        />
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label>Valor total máximo (R$)</label>
                        <input
                           type="number"
                           value={filterByMaxTotalAmount}
                           onChange={(e) =>
                              setFilterByMaxTotalAmount(e.target.value)
                           }
                           placeholder="0,00"
                           step="0.01"
                           min="0"
                        />
                     </Field>

                     <Field $col={6} $colMobile={12} $alignEnd>
                        <ActionBar>
                           <ActionBarRight>
                              <button
                                 type="button"
                                 onClick={handleClearFilters}
                                 disabled={isLoading}
                              >
                                 Limpar
                              </button>
                              <button type="submit" disabled={isLoading}>
                                 Pesquisar
                              </button>
                           </ActionBarRight>
                        </ActionBar>
                     </Field>
                  </FormGrid>
               </form>
            </SectionBody>
         </Section>

         {/* ─── Resultados ──────────────────────────────────────── */}
         {reportData && (
            <Section>
               <SectionHeader>
                  <SectionHeaderRow>
                     <SectionTitle>Resultados</SectionTitle>
                     <SectionMeta
                        items={[
                           {
                              key: 'employees',
                              label: 'Funcionários com débitos:',
                              value: reportData.total_employees_with_debts,
                           },
                           {
                              key: 'grand_total',
                              label: 'Total geral:',
                              value: formatCurrencyBRL(reportData.grand_total),
                           },
                        ]}
                     />
                  </SectionHeaderRow>
               </SectionHeader>
               {buildActiveFilterChips().length > 0 && (
                  <div
                     style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        padding: '0 0 12px',
                     }}
                  >
                     {buildActiveFilterChips().map((chip) => (
                        <ActiveFilterChip key={chip}>{chip}</ActiveFilterChip>
                     ))}
                  </div>
               )}
               <SectionBody>
                  {reportData.employees.length === 0 ? (
                     <EmptyState
                        title="Nenhum débito encontrado"
                        description="Nenhum funcionário possui parcelas para os filtros selecionados."
                     />
                  ) : (
                     <>
                        {/* ─── Controles de expansão e ordenação ──── */}
                        <div
                           style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '12px',
                              flexWrap: 'wrap',
                              gap: '8px',
                           }}
                        >
                           {/* Ordenação */}
                           <div
                              style={{
                                 display: 'flex',
                                 gap: '6px',
                                 alignItems: 'center',
                              }}
                           >
                              <span
                                 style={{
                                    fontSize: '0.75rem',
                                    color: '#9ca3af',
                                 }}
                              >
                                 Ordenar por:
                              </span>
                              <button
                                 type="button"
                                 onClick={() => handleSortAndFetch('full_name')}
                                 style={{ fontSize: '0.75rem' }}
                              >
                                 Nome{getSortIcon('full_name')}
                              </button>
                              <button
                                 type="button"
                                 onClick={() =>
                                    handleSortAndFetch('total_debts')
                                 }
                                 style={{ fontSize: '0.75rem' }}
                              >
                                 Valor total{getSortIcon('total_debts')}
                              </button>
                           </div>

                           {/* Expansão */}
                           <SectionActions
                              style={{ display: 'flex', gap: '8px' }}
                           >
                              <button type="button" onClick={handleExpandAll}>
                                 Expandir todos
                              </button>
                              <button type="button" onClick={handleCollapseAll}>
                                 Recolher todos
                              </button>
                              <button
                                 type="button"
                                 onClick={handleExportXlsx}
                                 disabled={!reportData?.employees?.length}
                              >
                                 Exportar CSV
                              </button>
                           </SectionActions>
                        </div>

                        {reportData.employees.map((employee) => {
                           const isExpanded = expandedEmployeeIds.has(
                              employee.employee_id
                           );

                           return (
                              <EmployeeCard key={employee.employee_id}>
                                 <EmployeeCardHeader
                                    $isExpanded={isExpanded}
                                    onClick={() =>
                                       handleToggleEmployeeExpansion(
                                          employee.employee_id
                                       )
                                    }
                                 >
                                    {/* ─── Info do funcionário ─── */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                       <p
                                          style={{
                                             margin: '0 0 2px',
                                             fontSize: '0.875rem',
                                             fontWeight: 500,
                                             color: '#111827',
                                          }}
                                       >
                                          {employee.full_name}
                                       </p>
                                       <p
                                          style={{
                                             margin: 0,
                                             fontSize: '0.75rem',
                                             color: '#6b7280',
                                          }}
                                       >
                                          {EMPLOYMENT_STATUS_LABEL[
                                             employee.employment_status
                                          ] ?? employee.employment_status}
                                          {employee.department_name &&
                                             ` · ${employee.department_name}`}
                                          {employee.city_location &&
                                             ` · ${employee.city_location}`}
                                       </p>
                                    </div>

                                    {/* ─── Totais ─── */}
                                    <div
                                       style={{
                                          display: 'flex',
                                          gap: '20px',
                                          alignItems: 'center',
                                          flexWrap: 'wrap',
                                       }}
                                    >
                                       {employee.total_salary_advances > 0 && (
                                          <div style={{ textAlign: 'right' }}>
                                             <p
                                                style={{
                                                   margin: 0,
                                                   fontSize: '0.75rem',
                                                   fontWeight: 500,
                                                   color: '#111827',
                                                }}
                                             >
                                                {formatCurrencyBRL(
                                                   employee.total_salary_advances
                                                )}
                                             </p>
                                             <p
                                                style={{
                                                   margin: 0,
                                                   fontSize: '0.65rem',
                                                   color: '#9ca3af',
                                                }}
                                             >
                                                Adiantamentos
                                             </p>
                                          </div>
                                       )}
                                       {employee.total_purchase_deductions >
                                          0 && (
                                          <div style={{ textAlign: 'right' }}>
                                             <p
                                                style={{
                                                   margin: 0,
                                                   fontSize: '0.75rem',
                                                   fontWeight: 500,
                                                   color: '#111827',
                                                }}
                                             >
                                                {formatCurrencyBRL(
                                                   employee.total_purchase_deductions
                                                )}
                                             </p>
                                             <p
                                                style={{
                                                   margin: 0,
                                                   fontSize: '0.65rem',
                                                   color: '#9ca3af',
                                                }}
                                             >
                                                Compras em folha
                                             </p>
                                          </div>
                                       )}
                                       {employee.total_benefit_vouchers > 0 && (
                                          <div style={{ textAlign: 'right' }}>
                                             <p
                                                style={{
                                                   margin: 0,
                                                   fontSize: '0.75rem',
                                                   fontWeight: 500,
                                                   color: '#111827',
                                                }}
                                             >
                                                {formatCurrencyBRL(
                                                   employee.total_benefit_vouchers
                                                )}
                                             </p>
                                             <p
                                                style={{
                                                   margin: 0,
                                                   fontSize: '0.65rem',
                                                   color: '#9ca3af',
                                                }}
                                             >
                                                Reembolso benefícios
                                             </p>
                                          </div>
                                       )}
                                       <TotalBadge>
                                          {formatCurrencyBRL(
                                             employee.total_debts
                                          )}
                                       </TotalBadge>
                                       <span
                                          style={{
                                             fontSize: '0.75rem',
                                             color: '#9ca3af',
                                             cursor: 'pointer',
                                          }}
                                          onClick={(e) => {
                                             e.stopPropagation();
                                             navigate(
                                                `/hr/employees/${employee.employee_id}`
                                             );
                                          }}
                                          title="Abrir ficha do funcionário"
                                       >
                                          ↗
                                       </span>
                                       <span
                                          style={{
                                             fontSize: '0.75rem',
                                             color: '#9ca3af',
                                          }}
                                       >
                                          {isExpanded ? '▲' : '▼'}
                                       </span>
                                    </div>
                                 </EmployeeCardHeader>

                                 {/* ─── Tabela de parcelas ─── */}
                                 {isExpanded && (
                                    <EmployeeCardBody>
                                       <TableWrapper>
                                          <Table>
                                             <thead>
                                                <tr>
                                                   <th>Tipo</th>
                                                   <th>Descrição</th>
                                                   <th>Parcela</th>
                                                   <th>Mês ref.</th>
                                                   <th
                                                      style={{
                                                         textAlign: 'right',
                                                      }}
                                                   >
                                                      Valor
                                                   </th>
                                                   <th />
                                                </tr>
                                             </thead>
                                             <tbody>
                                                {employee.installments.map(
                                                   (installment, index) => (
                                                      <tr
                                                         key={`${installment.type}-${installment.advance_id ?? installment.purchase_id}-${installment.installment_number}-${index}`}
                                                      >
                                                         <td>
                                                            <TypePill
                                                               $type={
                                                                  installment.type
                                                               }
                                                            >
                                                               {installment.type ===
                                                               'ADIANTAMENTO'
                                                                  ? 'Adiantamento'
                                                                  : installment.type ===
                                                                      'COMPRA_EM_FOLHA'
                                                                    ? 'Compra em folha'
                                                                    : 'Benefício'}
                                                            </TypePill>
                                                         </td>
                                                         <td>
                                                            {
                                                               installment.description
                                                            }
                                                         </td>
                                                         <td
                                                            style={{
                                                               textAlign:
                                                                  'center',
                                                            }}
                                                         >
                                                            {
                                                               installment.installment_number
                                                            }
                                                            /
                                                            {
                                                               installment.total_installments_count
                                                            }
                                                         </td>
                                                         <td
                                                            style={{
                                                               textAlign:
                                                                  'center',
                                                            }}
                                                         >
                                                            {formatMonthBR(
                                                               installment.reference_month
                                                            )}
                                                         </td>
                                                         <td
                                                            style={{
                                                               textAlign:
                                                                  'right',
                                                            }}
                                                         >
                                                            {formatCurrencyBRL(
                                                               installment.installment_amount
                                                            )}
                                                         </td>
                                                         <td>
                                                            {installment.type !==
                                                               'BENEFICIO' && (
                                                               <button
                                                                  type="button"
                                                                  style={{
                                                                     fontSize:
                                                                        '0.72rem',
                                                                     whiteSpace:
                                                                        'nowrap',
                                                                  }}
                                                                  onClick={(
                                                                     e
                                                                  ) => {
                                                                     e.stopPropagation();
                                                                     setDebtGroupBeingViewed(
                                                                        {
                                                                           debtType:
                                                                              installment.type,
                                                                           employeeId:
                                                                              employee.employee_id,
                                                                           employeeName:
                                                                              employee.full_name,
                                                                           groupId:
                                                                              installment.advance_id ??
                                                                              installment.purchase_id,
                                                                        }
                                                                     );
                                                                  }}
                                                               >
                                                                  Ver grupo
                                                               </button>
                                                            )}
                                                            {installment.type ===
                                                               'BENEFICIO' && (
                                                               <button
                                                                  type="button"
                                                                  style={{
                                                                     fontSize:
                                                                        '0.72rem',
                                                                     whiteSpace:
                                                                        'nowrap',
                                                                  }}
                                                                  onClick={(
                                                                     e
                                                                  ) => {
                                                                     e.stopPropagation();
                                                                     setBenefitVoucherBeingViewed(
                                                                        {
                                                                           voucherId:
                                                                              installment.voucher_id,
                                                                           employeeId:
                                                                              employee.employee_id,
                                                                           employeeName:
                                                                              employee.full_name,
                                                                        }
                                                                     );
                                                                  }}
                                                               >
                                                                  Ver benefício
                                                               </button>
                                                            )}
                                                         </td>
                                                      </tr>
                                                   )
                                                )}
                                             </tbody>
                                          </Table>
                                       </TableWrapper>
                                    </EmployeeCardBody>
                                 )}
                              </EmployeeCard>
                           );
                        })}
                     </>
                  )}
               </SectionBody>
            </Section>
         )}

         {/* ─── Modais de criação de débito ──────────────────── */}
         <SalaryAdvanceModal
            key={isAdvanceModalOpen ? 'advance-open' : 'advance-closed'}
            isOpen={isAdvanceModalOpen}
            onClose={() => setIsAdvanceModalOpen(false)}
            onSuccess={reportData ? fetchReport : undefined}
         />

         <PurchaseDeductionModal
            key={isPurchaseModalOpen ? 'purchase-open' : 'purchase-closed'}
            isOpen={isPurchaseModalOpen}
            onClose={() => setIsPurchaseModalOpen(false)}
            onSuccess={reportData ? fetchReport : undefined}
         />

         <BenefitVoucherModal
            key={isBenefitVoucherModalOpen ? 'benefit-open' : 'benefit-closed'}
            isOpen={isBenefitVoucherModalOpen}
            onClose={() => setIsBenefitVoucherModalOpen(false)}
            onSuccess={reportData ? fetchReport : undefined}
         />

         <MonthlyDeductionsModal
            isOpen={isMonthlyDeductionsModalOpen}
            onClose={() => setIsMonthlyDeductionsModalOpen(false)}
         />

         {/* ─── Modal de detalhe do grupo pai ───────────────── */}
         {debtGroupBeingViewed && (
            <DebtGroupDetailModal
               isOpen={Boolean(debtGroupBeingViewed)}
               onClose={() => setDebtGroupBeingViewed(null)}
               onSuccess={reportData ? fetchReport : undefined}
               debtType={debtGroupBeingViewed.debtType}
               employeeId={debtGroupBeingViewed.employeeId}
               employeeName={debtGroupBeingViewed.employeeName}
               groupId={debtGroupBeingViewed.groupId}
            />
         )}

         {/* ─── Modal de detalhe do benefício ───────────────── */}
         {benefitVoucherBeingViewed && (
            <BenefitVoucherDetailModal
               isOpen={Boolean(benefitVoucherBeingViewed)}
               onClose={() => setBenefitVoucherBeingViewed(null)}
               onSuccess={reportData ? fetchReport : undefined}
               voucherId={benefitVoucherBeingViewed.voucherId}
               employeeId={benefitVoucherBeingViewed.employeeId}
               employeeName={benefitVoucherBeingViewed.employeeName}
            />
         )}
      </Container>
   );
}
